import React from 'react';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { amountDisplay, isDarkModeOn, getMonthName, getUserTimezone } from '../../util/helper';
import { useLanguage } from '../../context/LanguageContext';

const MIN_TOUCHABLE_BAR_RATIO = 0.06;

const CategoryBarChart = ({
    categoryWiseExpense,
    titleKey = 'categoryWiseExpense',
    nameKey = 'expense',
    performanceMode = {},
    periodLabel = null,
}) => {
    const { language, t } = useLanguage();
    const isCompact = Boolean(performanceMode.compact);
    const disableAnimation = Boolean(performanceMode.disableAnimation);
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const currentMonth = dayjs().tz(getUserTimezone()).format('MMM');
    const resolvedPeriodLabel = periodLabel || getMonthName(currentMonth, t);

    const isDarkMode = isDarkModeOn();
    const labelColor = isDarkMode ? '#9ba3aa' : '#958f9f';
    const gridColor = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(149,143,159,0.18)';
    const categories = categoryWiseExpense.map((item) => item.category_name);
    const expenses = categoryWiseExpense.map((item) => Number(item.total_amount || 0));
    const maxExpense = Math.max(...expenses, 0);
    const minTouchableValue = maxExpense * MIN_TOUCHABLE_BAR_RATIO;
    // Keep tiny values tappable on touch screens while retaining exact value in tooltip/labels.
    const renderedExpenses = expenses.map((value) => {
        if (value <= 0 || maxExpense <= 0) return 0;
        return value < minTouchableValue ? minTouchableValue : value;
    });
    const chartHeight = isCompact
        ? Math.min(380, Math.max(240, categories.length * 30 + 56))
        : Math.min(560, Math.max(320, categories.length * 36 + 86));

    const colors = [
        '#622cfd', // Brand purple
        '#06BFFA', // Light Blue
        '#11a679', // Green
        '#FF8F3F', // Orange
        '#f10d5d', // Pink Red
        '#AF7CFF', // Purple
        '#00C9A7', // Aqua
        '#FFC75F', // Soft Yellow
        '#FF6F91', // Pinkish Red
        '#7173f9', // Indigo
        '#4ff6de', // Teal
        '#FF9671', // Light Coral
        '#845EC2', // Deep Purple
        '#0081CF', // Cerulean
        '#B39CD0', // Lavender
        '#FF6F61', // Tomato
        '#198754', // Bootstrap Green
        '#6FE79F', // Mint Green
        '#C34A36', // Brick Red
        '#4D8076', // Slate Green
    ];

    const series = [
        {
            name: t(nameKey),
            data: categories.map((cat, index) => ({
                x: `${cat}`,
                y: renderedExpenses[index],
            })),
        },
    ];

    const options = {
        chart: {
            type: 'bar',
            height: chartHeight,
            toolbar: { show: false },
            background: 'transparent',
            fontFamily: 'Lato, sans-serif',
            animations: {
                enabled: !disableAnimation,
                easing: 'easeinout',
                speed: disableAnimation ? 1 : 450,
                animateGradually: {
                    enabled: !disableAnimation,
                    delay: disableAnimation ? 0 : 40,
                },
            },
        },
        colors,
        plotOptions: {
            bar: {
                horizontal: true,
                borderRadius: 6,
                borderRadiusApplication: 'end',
                barHeight: '60%',
                distributed: true,
            },
        },
        dataLabels: {
            enabled: !isCompact,
            textAnchor: 'start',
            offsetX: 4,
            style: {
                colors: [isDarkMode ? '#e2e8f0' : '#373e3c'],
                fontSize: '11px',
                fontWeight: 600,
            },
            formatter: (_val, opts) =>
                `${amountDisplay(expenses[opts.dataPointIndex] || 0, language)}`,
            dropShadow: { enabled: false },
        },
        xaxis: {
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                formatter: (val) => `${amountDisplay(val, language)}`,
                style: {
                    fontSize: '10px',
                    colors: labelColor,
                },
            },
            tickAmount: 4,
        },
        yaxis: {
            labels: {
                maxWidth: 110,
                style: {
                    fontSize: '11px',
                    fontWeight: 500,
                    colors: isDarkMode ? '#e2e8f0' : '#373e3c',
                },
            },
        },
        tooltip: {
            theme: isDarkMode ? 'dark' : 'light',
            intersect: false,
            shared: false,
            y: {
                formatter: (_val, opts) =>
                    `৳${amountDisplay(expenses[opts.dataPointIndex] || 0, language)}`,
            },
        },
        states: {
            hover: { filter: { type: 'lighten', value: 0.06 } },
            active: { filter: { type: 'darken', value: 0.08 } },
        },
        legend: {
            show: false,
        },
        grid: {
            strokeDashArray: 4,
            borderColor: gridColor,
            xaxis: { lines: { show: true } },
            yaxis: { lines: { show: false } },
            padding: { left: 6, right: 12 },
        },
        responsive: [
            {
                breakpoint: 992,
                options: {
                    chart: { height: Math.min(460, Math.max(300, categories.length * 34 + 78)) },
                },
            },
            {
                breakpoint: 768,
                options: {
                    chart: { height: Math.min(380, Math.max(240, categories.length * 30 + 56)) },
                    plotOptions: {
                        bar: {
                            barHeight: '60%',
                            borderRadius: 5,
                        },
                    },
                    dataLabels: {
                        enabled: false,
                        style: {
                            fontSize: '10px',
                        },
                    },
                    xaxis: {
                        tickAmount: 3,
                        labels: {
                            formatter: (val) => `${amountDisplay(val, language)}`,
                            style: { fontSize: '9px' },
                        },
                    },
                    yaxis: {
                        labels: {
                            maxWidth: 90,
                            style: { fontSize: '10px' },
                        },
                    },
                    grid: { padding: { left: 2, right: 8 } },
                },
            },
            {
                breakpoint: 576,
                options: {
                    chart: { height: Math.min(340, Math.max(220, categories.length * 28 + 50)) },
                    plotOptions: {
                        bar: {
                            barHeight: '58%',
                        },
                    },
                    xaxis: {
                        tickAmount: 3,
                        labels: {
                            formatter: (val) => `${amountDisplay(val, language)}`,
                            style: { fontSize: '8px' },
                        },
                    },
                    yaxis: {
                        labels: {
                            maxWidth: 82,
                            style: { fontSize: '9px' },
                        },
                    },
                },
            },
        ],
    };

    return (
        <section>
            <div className="custom-container">
                <div className="chart">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="fw-semibold dark-text mb-0">
                            {t(titleKey)} |{' '}
                            <span className="light-text fw-normal">{resolvedPeriodLabel}</span>
                        </h3>
                        <ul className="listing mb-0">
                            <li>W</li>
                            <li className="active">M</li>
                            <li>Y</li>
                        </ul>
                    </div>
                    <Chart options={options} series={series} type="bar" height={chartHeight} />
                </div>
            </div>
        </section>
    );
};

export default CategoryBarChart;
